import { computed, type Ref } from 'vue'
import { useAccess } from '@/ContextTab/stores/auth/useAccess'
import { useNewsStore } from '@/ContextTab/stores/news'
import { ENewsStatus } from '@/lib/references'
import type { TNewsId } from '@/ContextTab/stores/types'


export function useNewsItem(id: Ref<TNewsId>) {

  const newsStore = useNewsStore()

  const newsItem = computed(() => newsStore.news[id.value] ?? null)
  const version = computed(() => newsItem.value?.version ?? null)

  const { userId } = useAccess()

  const newsItemIsRead = computed(() =>
    newsItem.value && newsItem.value.users?.length
      ? !!newsItem.value.users.find(
          (user) => user.user?.id === userId.value && user.is_read,
        )
      : false,
  )

  const newsItemIsScheduled = computed(() =>
    version.value
      ? version.value.publish_at !== null || version.value.is_postponed
      : false,
  )

  const newsItemIsExpired = computed(() => {
    return newsStore.expired.includes(id.value)
  })

  const newsItemUserCanWorkIds = computed(() =>
    newsItem.value?.users
      ?.filter((binding) => binding.can_work && Boolean(binding.user))
      .map(({ user }) => user!.id) ?? [],
  )

  const newsItemIsAssignedToMe = computed(() =>
    version.value && newsItemIsReadyToEdit.value
      ? !!newsItemUserCanWorkIds.value.find(
          (id) => id === userId.value,
        )
      : false,
  )

  const newsItemUserCanWork = computed(() =>
    userId.value && newsItem.value
      ? newsItemUserCanWorkIds.value.includes(userId.value)
      : false,
  )

  const newsItemUserCanEdit = computed(() => {
    const statusId = version.value?.status_id

    if (!version.value || !statusId) {
      return false
    }

    const status = ENewsStatus[statusId]

    const newsItemEditableByStatus = ['DRAFT', 'ON_EDIT'].includes(status)
    const userCanWorkWithNewsItem = newsItemUserCanWork.value
    const userIsAVersionOwner
          = version.value.user?.id === userId.value

    return newsItemEditableByStatus && (userCanWorkWithNewsItem || userIsAVersionOwner)
  })

  const newsItemIsReadyToEdit = computed(() => {
    const statusId = version.value?.status_id
    if (!statusId) {
      return false
    }
    const statusName = ENewsStatus[statusId]
    return ['WAITS_FOR_EDIT'].includes(statusName)
  })

  const newsItemIsRevoked = computed(() => newsItem.value?.is_revoked ?? false)

  async function setNewsItemAsRead() {
    if (
      newsItemIsReadyToEdit.value
      && !newsItemIsRead.value
    ) {
      await newsStore.setNewsItemAsRead(id.value)
    }
  }

  return {
    newsItemIsRead,
    newsItemIsScheduled,
    newsItemIsExpired,
    newsItemIsAssignedToMe,
    newsItemIsReadyToEdit,
    newsItemUserCanEdit,
    newsItemIsRevoked,
    setNewsItemAsRead,
  }
}
